import * as fbPixel from './facebook-pixel';
import { getVariant } from './abTest';
import { getKeywordVariant } from './hooks/useKeywordVariant';

// Lazy-loaded ReactGA - keeps react-ga4 out of the critical bundle
let ReactGA = null;

// Event queue — buffers events fired before GA is ready, flushed on init
let _eventQueue = [];

// Initialize Google Analytics (async)
export const initGA = async () => {
  const trackingId = process.env.REACT_APP_GA_TRACKING_ID;

  if (trackingId) {
    try {
      const mod = await import('react-ga4');
      ReactGA = mod.default;
      ReactGA.initialize(trackingId);

      // Set seo_headline_variant as a GA4 user property so it's attached to ALL events
      const variant = getKeywordVariant();
      if (variant) {
        ReactGA.gtag('set', 'user_properties', { seo_headline_variant: variant });
      }

      console.log('📊 Google Analytics initialized');

      // Flush queued events
      if (_eventQueue.length > 0) {
        console.log(`📊 Flushing ${_eventQueue.length} queued events`);
        _eventQueue.forEach(({ name, params }) => ReactGA.event(name, params));
        _eventQueue = [];
      }
    } catch (err) {
      console.log('⚠️ Failed to load GA:', err);
    }
  } else {
    console.log('⚠️ GA Tracking ID not found - running without analytics');
  }
};

// Track page views
export const trackPageView = (pageName) => {
  if (!ReactGA) return;
  ReactGA.send({ hitType: 'pageview', page: `/${pageName}`, title: pageName });
  console.log(`📊 Page view: ${pageName}`);
};

// Safe event helper — queues events if GA hasn't loaded yet
const gaEvent = (name, params) => {
  if (ReactGA) {
    ReactGA.event(name, params);
  } else {
    _eventQueue.push({ name, params });
  }
};

/**
 * Extract the SEO source keyword that drove this session.
 * Checks: ?keyword= param > ?utm_content= > document.referrer organic query > null
 * Store in sessionStorage so it persists across page nav within the session.
 */
const _getSourceKeyword = () => {
  try {
    const cached = sessionStorage.getItem('pickify_source_keyword');
    if (cached) return cached;

    const params = new URLSearchParams(window.location.search);
    const keyword = params.get('keyword') || params.get('utm_content') || null;

    if (keyword) {
      sessionStorage.setItem('pickify_source_keyword', keyword);
      return keyword;
    }

    // Try to extract from organic referrer (Google/Bing)
    const ref = document.referrer;
    if (ref && (ref.includes('google.') || ref.includes('bing.'))) {
      try {
        const refUrl = new URL(ref);
        const q = refUrl.searchParams.get('q');
        if (q) {
          sessionStorage.setItem('pickify_source_keyword', q);
          return q;
        }
      } catch { /* invalid URL */ }
    }
  } catch { /* sessionStorage unavailable */ }
  return null;
};

// Analytics tracking functions with proper GA4 event names
export const analytics = {
  // Quiz events
  quizStarted: () => {
    gaEvent('quiz_started', {
      event_category: 'Quiz',
      event_label: 'User started quiz',
      seo_headline_variant: getKeywordVariant(),
      source_keyword: _getSourceKeyword(),
    });
    fbPixel.trackQuizStarted(); // Enhanced with CAPI
    // Store start time for duration tracking
    sessionStorage.setItem('pickify_quiz_start', Date.now().toString());
    console.log('📊 Event: quiz_started');
  },

  quizQuestionAnswered: (questionNumber, questionId, answer) => {
    gaEvent('quiz_question_answered', {
      event_category: 'Quiz',
      question_number: questionNumber,
      question_id: questionId,
      question_name: questionId, // For funnel breakdown
      answer: Array.isArray(answer) ? answer.join(',') : answer
    });
    console.log(`📊 Event: quiz_question_answered - Q${questionNumber}: ${questionId}`);
  },

  quizCompleted: (answers) => {
    // Calculate time spent on quiz
    const startTime = sessionStorage.getItem('pickify_quiz_start');
    const timeSpent = startTime ? Math.round((Date.now() - parseInt(startTime)) / 1000) : null;

    gaEvent('quiz_completed', {
      event_category: 'Quiz',
      recipient: answers.recipient,
      occasion: answers.occasion,
      budget: answers.budget,
      time_spent_seconds: timeSpent,
      seo_headline_variant: getKeywordVariant(),
      event_label: 'Quiz completed successfully'
    });
    fbPixel.trackQuizCompleted(answers); // Enhanced with CAPI + Lead event

    // Clear start time
    sessionStorage.removeItem('pickify_quiz_start');
    console.log(`📊 Event: quiz_completed (${timeSpent}s)`);
  },

  quizAbandoned: (questionNumber, questionId) => {
    gaEvent('quiz_abandoned', {
      event_category: 'Quiz',
      question_number: questionNumber,
      question_id: questionId,
      event_label: `Abandoned at question ${questionNumber}`
    });
    console.log(`📊 Event: quiz_abandoned at Q${questionNumber}`);
  },

  // Results events
  resultsViewed: (giftCount) => {
    gaEvent('results_viewed', {
      event_category: 'Results',
      gift_count: giftCount,
      ab_variant: getVariant('results_view'),
      event_label: 'User viewed gift recommendations'
    });
    fbPixel.trackResultsViewed(giftCount); // Enhanced with CAPI + ViewContent
    console.log(`📊 Event: results_viewed - ${giftCount} gifts`);
  },

  // Conversion events
  buyNowClicked: (giftTitle, price, giftIndex, storeName = 'Unknown') => {
    gaEvent('buy_now_clicked', {
      event_category: 'Conversion',
      gift_title: giftTitle,
      gift_price: price,
      gift_position: giftIndex + 1,
      store_name: storeName,
      value: price,
      currency: 'USD',
      ab_variant: getVariant('results_view'),
      seo_headline_variant: getKeywordVariant(),
      source_keyword: _getSourceKeyword(),
      event_label: giftTitle
    });
    // Enhanced CAPI tracking with affiliate click data
    fbPixel.trackAffiliateClick(giftTitle, price, storeName, giftIndex + 1);
    console.log(`📊 Event: buy_now_clicked - ${giftTitle} ($${price}) on ${storeName}`);
  },

  // Cache events
  cacheUsed: () => {
    gaEvent('cache_used', {
      event_category: 'Performance',
      event_label: 'Saved API call'
    });
    console.log('📊 Event: cache_used');
  },

  // Navigation events
  restartClicked: () => {
    gaEvent('restart_clicked', {
      event_category: 'Navigation',
      event_label: 'New search started'
    });
    console.log('📊 Event: restart_clicked');
  },

  // Social sharing events
  resultsShared: (platform) => {
    gaEvent('results_shared', {
      event_category: 'Social',
      platform: platform,
      event_label: `Shared via ${platform}`
    });
    console.log(`📊 Event: results_shared - ${platform}`);
  },

  // Save events
  resultsSaved: () => {
    gaEvent('results_saved', {
      event_category: 'Engagement',
      event_label: 'Results saved for later'
    });
    console.log('📊 Event: results_saved');
  },

  // Print events
  resultsPrinted: () => {
    gaEvent('results_printed', {
      event_category: 'Engagement',
      event_label: 'Results printed/exported as PDF'
    });
    console.log('📊 Event: results_printed');
  },

  // Email subscription events
  emailSubscribed: (source) => {
    gaEvent('email_subscribed', {
      event_category: 'Conversion',
      source: source,
      event_label: `Email subscribed from ${source}`
    });
    fbPixel.trackLead(source);
    console.log(`📊 Event: email_subscribed - ${source}`);
  },

  // Referral tracking events
  referralLanded: (refCode, source) => {
    gaEvent('referral_landed', {
      event_category: 'Referral',
      ref_code: refCode,
      source: source,
      event_label: `Landed via referral: ${refCode}`
    });
    console.log(`📊 Event: referral_landed - ${refCode} from ${source}`);
  },

  referralConverted: (refCode) => {
    gaEvent('referral_converted', {
      event_category: 'Referral',
      ref_code: refCode,
      event_label: `Referral converted: ${refCode}`
    });
    console.log(`📊 Event: referral_converted - ${refCode}`);
  },

  // "See More Ideas" funnel events
  seeMoreClicked: (currentGiftCount) => {
    gaEvent('see_more_clicked', {
      event_category: 'Engagement',
      current_gift_count: currentGiftCount,
      ab_variant: getVariant('results_view'),
      event_label: 'User clicked See More Ideas'
    });
    fbPixel.trackCustom('SeeMoreClicked', {
      current_gift_count: currentGiftCount,
    });
    console.log(`📊 Event: see_more_clicked (had ${currentGiftCount} gifts)`);
  },

  seeMoreGiftClicked: (giftTitle, price, position, storeName = 'Unknown') => {
    gaEvent('see_more_gift_clicked', {
      event_category: 'Conversion',
      gift_title: giftTitle,
      gift_price: price,
      gift_position: position,
      store_name: storeName,
      is_extra_gift: true,
      event_label: `Extra gift clicked: ${giftTitle}`
    });
    // Enhanced CAPI tracking with affiliate click data
    fbPixel.trackAffiliateClick(giftTitle, price, storeName, position);
    console.log(`📊 Event: see_more_gift_clicked - ${giftTitle} (position ${position}) on ${storeName}`);
  },

  // Blog engagement events
  blogViewed: (slug, title) => {
    gaEvent('blog_viewed', {
      event_category: 'Content',
      blog_slug: slug,
      blog_title: title,
      event_label: `Blog viewed: ${title}`
    });
    fbPixel.trackViewContent(title);
    console.log(`📊 Event: blog_viewed - ${slug}`);
  },

  blogCTAClicked: (slug, ctaType) => {
    gaEvent('blog_cta_clicked', {
      event_category: 'Conversion',
      blog_slug: slug,
      cta_type: ctaType,
      event_label: `Blog CTA clicked: ${ctaType} from ${slug}`
    });
    fbPixel.trackCustom('BlogCTAClicked', {
      blog_slug: slug,
      cta_type: ctaType,
    });
    console.log(`📊 Event: blog_cta_clicked - ${ctaType} from ${slug}`);
  },

  // Currency/locale events
  currencyChanged: (fromCurrency, toCurrency) => {
    gaEvent('currency_changed', {
      event_category: 'Settings',
      from_currency: fromCurrency,
      to_currency: toCurrency,
      event_label: `Currency changed: ${fromCurrency} → ${toCurrency}`
    });
    console.log(`📊 Event: currency_changed - ${fromCurrency} → ${toCurrency}`);
  },

  // Gift visibility tracking (for scroll depth analysis)
  giftViewed: (giftIndex, giftTitle, isExtraGift = false) => {
    gaEvent('gift_viewed', {
      event_category: 'Engagement',
      gift_index: giftIndex,
      gift_title: giftTitle,
      is_extra_gift: isExtraGift,
      ab_variant: getVariant('results_view'),
      event_label: `Gift viewed: ${giftTitle}`
    });
    console.log(`📊 Event: gift_viewed - #${giftIndex + 1} ${giftTitle}`);
  },

  // Error tracking
  apiError: (endpoint, errorType, errorMessage) => {
    gaEvent('api_error', {
      event_category: 'Error',
      endpoint: endpoint,
      error_type: errorType,
      error_message: errorMessage,
      event_label: `API Error: ${endpoint} - ${errorType}`
    });
    console.log(`📊 Event: api_error - ${endpoint}: ${errorType}`);
  },

  // Deal CTA Click tracking - for BigQuery analysis
  // cta_location: 'deal_teaser' | 'deal_zone' | 'results_page'
  dealCtaClicked: (deal, ctaLocation) => {
    gaEvent('deal_cta_clicked', {
      event_category: 'Conversion',
      cta_location: ctaLocation,
      deal_source: deal.source,
      deal_title: deal.title,
      deal_price: deal.salePrice,
      deal_discount: deal.discountPercent,
      deal_category: deal.displayCategory || deal.occasionValue,
      currency: deal.currency || 'USD',
      value: deal.salePrice,
      event_label: `${ctaLocation}: ${deal.title}`
    });
    console.log(`📊 Event: deal_cta_clicked - ${ctaLocation}: ${deal.title} ($${deal.salePrice})`);
  },

  // ============================================================
  // Personalization Events (Task 4: Feedback Loop)
  // ============================================================

  // Fired when a boosted/personalized result is shown to user
  personalizedView: (category, source, itemCount) => {
    gaEvent('personalized_view', {
      event_category: 'Personalization',
      boosted_category: category,
      personalization_source: source, // 'inbound' or 'behavior'
      boosted_item_count: itemCount,
      event_label: `Personalized results shown: ${category}`
    });
    console.log(`📊 Event: personalized_view - category: ${category}, source: ${source}, items: ${itemCount}`);
  },

  // Fired when user clicks "Buy Now" on a personalized/boosted item
  personalizedClick: (category, source, itemTitle, itemPosition) => {
    gaEvent('personalized_click', {
      event_category: 'Personalization',
      boosted_category: category,
      personalization_source: source, // 'inbound' or 'behavior'
      item_title: itemTitle,
      item_position: itemPosition,
      event_label: `Personalized click: ${itemTitle} (${category})`
    });
    console.log(`📊 Event: personalized_click - ${itemTitle} (category: ${category}, position: ${itemPosition})`);
  },

  // Fired when inbound signal is captured from ad URL parameter
  inboundSignalCaptured: (category, utmSource, utmCampaign) => {
    gaEvent('inbound_signal_captured', {
      event_category: 'Personalization',
      interest_category: category,
      utm_source: utmSource,
      utm_campaign: utmCampaign,
      event_label: `Ad signal: ${category} from ${utmSource}`
    });
    console.log(`📊 Event: inbound_signal_captured - ${category} from ${utmSource}`);
  },

  // ============================================================
  // Swipe A/B Test Events
  // ============================================================

  abTestAssigned: (testName, variant) => {
    gaEvent('ab_test_assigned', {
      event_category: 'Experiment',
      test_name: testName,
      variant: variant,
      event_label: `${testName}: ${variant}`
    });
    console.log(`📊 Event: ab_test_assigned - ${testName}: ${variant}`);
  },

  swipeRight: (giftTitle, giftPosition, storeName) => {
    gaEvent('swipe_right', {
      event_category: 'Swipe',
      gift_title: giftTitle,
      gift_position: giftPosition,
      store_name: storeName,
      ab_variant: 'swipe',
      event_label: `Swiped right: ${giftTitle}`
    });
    console.log(`📊 Event: swipe_right - ${giftTitle} (position ${giftPosition})`);
  },

  swipeLeft: (giftTitle, giftPosition, storeName) => {
    gaEvent('swipe_left', {
      event_category: 'Swipe',
      gift_title: giftTitle,
      gift_position: giftPosition,
      store_name: storeName,
      ab_variant: 'swipe',
      event_label: `Swiped left: ${giftTitle}`
    });
    console.log(`📊 Event: swipe_left - ${giftTitle} (position ${giftPosition})`);
  },

  swipeUndo: (giftTitle, giftPosition) => {
    gaEvent('swipe_undo', {
      event_category: 'Swipe',
      gift_title: giftTitle,
      gift_position: giftPosition,
      ab_variant: 'swipe',
      event_label: `Undo swipe: ${giftTitle}`
    });
    console.log(`📊 Event: swipe_undo - ${giftTitle} (position ${giftPosition})`);
  },

  swipeCardExpanded: (giftTitle, giftPosition) => {
    gaEvent('swipe_card_expanded', {
      event_category: 'Swipe',
      gift_title: giftTitle,
      gift_position: giftPosition,
      ab_variant: 'swipe',
      event_label: `Card expanded: ${giftTitle}`
    });
    console.log(`📊 Event: swipe_card_expanded - ${giftTitle} (position ${giftPosition})`);
  },

  swipeSummaryViewed: (likedCount, totalCount) => {
    gaEvent('swipe_summary_viewed', {
      event_category: 'Swipe',
      liked_count: likedCount,
      total_count: totalCount,
      ab_variant: 'swipe',
      event_label: `Summary: ${likedCount}/${totalCount} liked`
    });
    console.log(`📊 Event: swipe_summary_viewed - ${likedCount}/${totalCount} liked`);
  },

  swipeSummaryClick: (giftTitle, wasLiked) => {
    gaEvent('swipe_summary_click', {
      event_category: 'Swipe',
      gift_title: giftTitle,
      was_liked: wasLiked,
      ab_variant: 'swipe',
      event_label: `Summary click: ${giftTitle} (${wasLiked ? 'liked' : 'skipped'})`
    });
    console.log(`📊 Event: swipe_summary_click - ${giftTitle} (${wasLiked ? 'liked' : 'skipped'})`);
  },

  // User rating submitted
  userRated: (rating) => {
    gaEvent('user_rated', {
      event_category: 'Feedback',
      rating: rating,
      rating_label: rating >= 4 ? 'positive' : rating >= 3 ? 'neutral' : 'negative',
      value: rating,
      event_label: `User rated ${rating} stars`
    });
    console.log(`📊 Event: user_rated - ${rating} stars`);
  },

  // ============================================================
  // App Download CTAs
  // placement: 'results_card' | 'sticky_banner' | 'footer' | 'welcome_secondary'
  // source_screen: derived from URL path so we can attribute installs to
  //   the page the user was on when they clicked.
  // ============================================================

  appDownloadCtaViewed: (placement) => {
    const ctx = _getAppDownloadContext();
    gaEvent('app_download_cta_viewed', {
      event_category: 'AppDownload',
      placement: placement,
      source_screen: ctx.source_screen,
      page_path: ctx.page_path,
      page_title: ctx.page_title,
      event_label: `App CTA viewed: ${placement} on ${ctx.source_screen}`
    });
    console.log(`📊 Event: app_download_cta_viewed - ${placement} on ${ctx.source_screen}`);
  },

  appDownloadClicked: (placement, platform) => {
    const ctx = _getAppDownloadContext();
    gaEvent('app_download_clicked', {
      event_category: 'AppDownload',
      placement: placement,
      platform: platform, // 'ios' | 'android'
      source_screen: ctx.source_screen,
      page_path: ctx.page_path,
      page_title: ctx.page_title,
      event_label: `App download: ${platform} from ${placement} on ${ctx.source_screen}`
    });
    fbPixel.trackCustom('AppDownloadClicked', {
      placement,
      platform,
      source_screen: ctx.source_screen,
      page_path: ctx.page_path,
    });
    console.log(`📊 Event: app_download_clicked - ${platform} from ${placement} on ${ctx.source_screen}`);
  },

  appDownloadDismissed: (placement) => {
    const ctx = _getAppDownloadContext();
    gaEvent('app_download_dismissed', {
      event_category: 'AppDownload',
      placement: placement,
      source_screen: ctx.source_screen,
      page_path: ctx.page_path,
      event_label: `App CTA dismissed: ${placement} on ${ctx.source_screen}`
    });
    console.log(`📊 Event: app_download_dismissed - ${placement} on ${ctx.source_screen}`);
  }
};

/**
 * Build context for App Download analytics events.
 * Derives a stable `source_screen` semantic label from the URL path so installs
 * can be attributed to the page the user came from, regardless of placement.
 *
 * Examples:
 *   /                          → 'home_welcome' | 'home_quiz' | 'home_results'
 *   /deals                     → 'deal_zone'
 *   /gifts/gifts-for-mom       → 'hub_gifts-for-mom'
 *   /blog                      → 'blog_index'
 *   /blog/best-ai-gift-finders → 'blog_post'
 *   /r/abc123                  → 'shared_results'
 *   /reviews                   → 'reviews'
 *   /ai-gift-finder            → 'ai_gift_finder'
 *   /knowledge-base/foo        → 'knowledge_base'
 *   /insights/...              → 'insights'
 *   /privacy                   → 'privacy'
 *   any other                  → 'other'
 */
function _getAppDownloadContext() {
  if (typeof window === 'undefined') {
    return { page_path: 'unknown', page_title: 'unknown', source_screen: 'unknown' };
  }
  const path = window.location.pathname || '/';
  const title = (typeof document !== 'undefined' && document.title) ? document.title : 'unknown';
  let source_screen = 'other';

  try {
    if (path === '/') {
      // Home page has three states tracked in localStorage
      const quizComplete = localStorage.getItem('pickify_quiz_complete');
      const quizProgress = localStorage.getItem('pickify_quiz_progress');
      if (quizComplete) source_screen = 'home_results';
      else if (quizProgress) source_screen = 'home_quiz';
      else source_screen = 'home_welcome';
    } else if (path === '/deals') {
      source_screen = 'deal_zone';
    } else if (path.startsWith('/gifts/')) {
      const slug = path.slice('/gifts/'.length).split('/')[0] || 'unknown';
      source_screen = `hub_${slug}`;
    } else if (path === '/blog') {
      source_screen = 'blog_index';
    } else if (path.startsWith('/blog/')) {
      source_screen = 'blog_post';
    } else if (path.startsWith('/r/')) {
      source_screen = 'shared_results';
    } else if (path === '/reviews') {
      source_screen = 'reviews';
    } else if (path.startsWith('/knowledge-base/')) {
      source_screen = 'knowledge_base';
    } else if (path.startsWith('/insights/')) {
      source_screen = 'insights';
    } else if (path === '/ai-gift-finder') {
      source_screen = 'ai_gift_finder';
    } else if (path === '/privacy') {
      source_screen = 'privacy';
    } else if (path === '/admin') {
      source_screen = 'admin';
    }
  } catch { /* localStorage unavailable */ }

  return { page_path: path, page_title: title, source_screen };
}

// Referral tracking utility
export const referralTracker = {
  // Capture referral from URL on page load
  captureReferral: () => {
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref');
    const utm_source = urlParams.get('utm_source');
    const utm_medium = urlParams.get('utm_medium');
    const utm_campaign = urlParams.get('utm_campaign');

    if (ref) {
      const referralData = {
        code: ref,
        source: utm_source || 'direct',
        medium: utm_medium || 'referral',
        campaign: utm_campaign || null,
        landedAt: new Date().toISOString(),
        landingPage: window.location.pathname
      };
      localStorage.setItem('pickify_referral', JSON.stringify(referralData));
      analytics.referralLanded(ref, utm_source || 'direct');
      console.log('🔗 Referral captured:', referralData);
      return referralData;
    }

    // Also capture UTM params without ref code
    if (utm_source) {
      const utmData = {
        source: utm_source,
        medium: utm_medium || 'unknown',
        campaign: utm_campaign || null,
        landedAt: new Date().toISOString()
      };
      localStorage.setItem('pickify_utm', JSON.stringify(utmData));
      console.log('📈 UTM params captured:', utmData);
    }

    return null;
  },

  // Get stored referral data
  getReferral: () => {
    const stored = localStorage.getItem('pickify_referral');
    return stored ? JSON.parse(stored) : null;
  },

  // Mark referral as converted (call when quiz is completed)
  markConverted: () => {
    const referral = referralTracker.getReferral();
    if (referral && !referral.converted) {
      referral.converted = true;
      referral.convertedAt = new Date().toISOString();
      localStorage.setItem('pickify_referral', JSON.stringify(referral));
      analytics.referralConverted(referral.code);
      console.log('✅ Referral marked as converted:', referral.code);
      return true;
    }
    return false;
  },

  // Generate a shareable referral link
  generateReferralLink: (userIdentifier) => {
    const baseUrl = window.location.origin;
    const refCode = userIdentifier || `user_${Date.now().toString(36)}`;
    return `${baseUrl}/?ref=${refCode}`;
  }
};
